import { type ReactNode, useMemo } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

const stripePromises = new Map<string, Promise<Stripe | null>>();

/**
 * Lazily load Stripe.js exactly once per publishable key, outside of render.
 */
function getStripePromise(publishableKey: string): Promise<Stripe | null> {
  let promise = stripePromises.get(publishableKey);
  if (!promise) {
    promise = loadStripe(publishableKey);
    stripePromises.set(publishableKey, promise);
  }
  return promise;
}

interface StripeElementsWrapperProps {
  clientSecret: string;
  publishableKey: string;
  children: ReactNode;
}

/**
 * Wraps children in Stripe <Elements> with the given SetupIntent clientSecret.
 * Stripe.js is loaded once per publishable key and reused across dialog opens.
 */
export function StripeElementsWrapper({
  clientSecret,
  publishableKey,
  children,
}: StripeElementsWrapperProps): JSX.Element {
  const stripe = useMemo(() => getStripePromise(publishableKey), [publishableKey]);

  const options = useMemo(
    () => ({
      clientSecret,
      appearance: {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: '#228be6',
          borderRadius: '8px',
          fontFamily: 'inherit',
        },
      },
    }),
    [clientSecret]
  );

  // New clientSecret = new SetupIntent; remount Elements so Stripe iframes and test-mode hints reset.
  return (
    <Elements key={clientSecret} stripe={stripe} options={options}>
      {children}
    </Elements>
  );
}
